import './ModalContent.scss';

import clx from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useResetCategoryAutoSelect } from '../../hooks/useResetCategoryAutoSelect';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { resetCarouselFormLoadedOnce, setCarouselViewOff } from '../../store/slices/carousel';
import { getPlatformsViewportSelector, hideNavSelector } from '../../store/slices/global';
import { internalNavigateBack } from '../../utils/navigationControl';
import Header from '../components/Header/Header';
import { useSetHeaderConfig } from '../components/Header/hooks/useSetHeaderConfig';

type Props = {
  children: ReactNode;
  labelledById?: string;
};

export const ModalContent = ({ children, labelledById }: Props) => {
  const hideNav = useAppSelector(hideNavSelector);
  const platformsViewport = useAppSelector(getPlatformsViewportSelector);
  const ref = useRef<HTMLDivElement>(null);
  const root = document.getElementById('root');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resetCategoryAutoSelect = useResetCategoryAutoSelect();

  // Use modalHeight from platform if available, otherwise fall back to root height
  const modalHeight = platformsViewport?.modalHeight
    ? `${platformsViewport.modalHeight}px`
    : `${root?.getBoundingClientRect().height ?? 0}px`;

  const handleCloseModal = () => {
    resetCategoryAutoSelect();
    dispatch(setCarouselViewOff());
    dispatch(resetCarouselFormLoadedOnce());
    internalNavigateBack(navigate);
  };

  useSetHeaderConfig({
    onCloseAction: handleCloseModal,
    onNavigationAction: handleCloseModal,
  });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const focusable = node.querySelectorAll<HTMLElement>(
      'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])',
    );
    focusable[0]?.focus();
  }, []);

  return createPortal(
    <div
      id="modal-root"
      role="dialog"
      aria-modal="true"
      nonce={window.__webpack_nonce__}
      style={{
        minHeight: modalHeight,
      }}
      className={clx('modal-content-wrapper', {
        'no-header': hideNav,
      })}
      aria-labelledby={labelledById}
    >
      <div className="modal-content-header-wrapper">
        <Header />
      </div>
      <div className="modal-content-body-wrapper">
        <div ref={ref} className="modal-content-body">
          {children}
        </div>
      </div>
    </div>,
    root || document.body,
  ) as React.ReactElement;
};
